import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { apiRequest } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export interface PinnedImage {
  url: string;
  label: string;
  createdAt: string;
}

export interface Customer {
  id: string;
  name: string;
  phone: string;
  email: string;
  status: "new" | "active" | "hot_lead" | "stalled" | "closed";
  budget: string;
  needs: string;
  notes: string;
  lastContact: string;
  createdAt: string;
  assignedTo: string;
  pinnedImages?: PinnedImage[];
}

interface CustomerContextType {
  customers: Customer[];
  addCustomer: (c: Omit<Customer, "id" | "createdAt" | "lastContact">) => Promise<Customer>;
  updateCustomer: (id: string, data: Partial<Customer>) => Promise<void>;
  deleteCustomer: (id: string) => Promise<void>;
  getCustomer: (id: string) => Customer | undefined;
  refreshCustomers: () => Promise<void>;
}

const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

export function CustomerProvider({ children }: { children: ReactNode }) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const { user, isAuthenticated } = useAuth();

  const refreshCustomers = useCallback(async () => {
    if (!isAuthenticated || !user?.storeId) {
      setCustomers([]);
      return;
    }
    try {
      const data = await apiRequest<Customer[]>("/customers");
      setCustomers(data);
    } catch {
      setCustomers([]);
    }
  }, [isAuthenticated, user?.storeId]);

  useEffect(() => {
    refreshCustomers();
  }, [refreshCustomers]);

  const addCustomer = async (data: Omit<Customer, "id" | "createdAt" | "lastContact">): Promise<Customer> => {
    const newCustomer = await apiRequest<Customer>("/customers", {
      method: "POST",
      body: JSON.stringify(data),
    });
    setCustomers((prev) => [newCustomer, ...prev]);
    return newCustomer;
  };

  const updateCustomer = async (id: string, data: Partial<Customer>) => {
    const updated = await apiRequest<Customer>(`/customers/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    setCustomers((prev) => prev.map((c) => (c.id === id ? updated : c)));
  };

  const deleteCustomer = async (id: string) => {
    await apiRequest(`/customers/${id}`, { method: "DELETE" });
    setCustomers((prev) => prev.filter((c) => c.id !== id));
  };

  const getCustomer = (id: string) => customers.find((c) => c.id === id);

  return (
    <CustomerContext.Provider value={{ customers, addCustomer, updateCustomer, deleteCustomer, getCustomer, refreshCustomers }}>
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomers() {
  const ctx = useContext(CustomerContext);
  if (!ctx) throw new Error("useCustomers must be used within CustomerProvider");
  return ctx;
}
